import {
  Alert,
  Box,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";

import { useAuth, useNavigation } from "@/providers";
import {
  ActionParameter,
  ComputeRequirements,
  ContainerParameters,
} from "@/shared/domain/actions";
import {
  useAction,
  useUpdateScheduledTrigger,
  useUpdateTrigger,
} from "@/shared/domain/actions/hooks";
import {
  TriggerType,
  TriggerView,
  UpdateTriggerRequest,
  UpdateScheduledTriggerRequest,
} from "@/shared/domain/actions/TriggerRecord";
import { OrganizationTier, OrgRecord } from "@/shared/domain/orgs";

import EditableActionParams from "../action/EditableActionParams";
import EditableComputeReqs from "../action/EditableComputeReqs";
import EditableContainerParams from "../action/EditableContainerParams";
import { EditableOutputDataset } from "../action/EditableOutputDataset";
import { EditableTimeout } from "../action/EditableTimeout";

import DeleteTriggerButton from "./DeleteTriggerButton";
import { EditableActionSelector } from "./EditableActionSelector";
import { EditableForEachSelector } from "./EditableForEachSelector";
import { EditableInputData } from "./EditableInputData";
import { EditableInvocationInput } from "./EditableInvocationInput";
import { EditableTriggerCauses } from "./EditableTriggerCauses";
import { EditableTriggerCondition } from "./EditableTriggerCondition/EditableTriggerCondition";
import { EditableTriggerSchedule } from "./EditableTriggerSchedule";
import { TriggerSwitch } from "./TriggerSwitch";

interface TriggerDetailsProps {
  org: OrgRecord;
  trigger: TriggerView;
}

export const TriggerDetails: React.FC<TriggerDetailsProps> = ({
  org,
  trigger,
}) => {
  const [errorText, setErrorText] = React.useState<string | null>(null);
  const [parameters, setParameters] = React.useState<
    ActionParameter[] | undefined
  >(undefined);
  const { currentOrganization } = useAuth();
  const { goto } = useNavigation();

  const theme = useTheme();

  const updateTriggerMutation = useUpdateTrigger();
  const updateScheduledTriggerMutation = useUpdateScheduledTrigger();

  const actionQuery = useAction(trigger.action.name, trigger.action.digest, {
    resourceOwnerId: trigger.action.owner || currentOrganization?.org_id,
  });

  React.useEffect(() => {
    if (actionQuery.data) {
      setParameters(actionQuery.data?.parameters);
    }
  }, [trigger, currentOrganization?.org_id, actionQuery.data]);

  function handleEventTriggerUpdate(updates: UpdateTriggerRequest) {
    if (!org?.org_id || trigger.trigger_type !== TriggerType.EventDriven) {
      return;
    }

    updateTriggerMutation.mutate(
      {
        name: trigger.name,
        resourceOwnerId: org.org_id,
        updates,
      },
      {
        onError: (err) => setErrorText(err.message),
      },
    );
  }

  function handleScheduledTriggerUpdate(
    updates: UpdateScheduledTriggerRequest,
  ) {
    if (trigger.trigger_type !== TriggerType.Scheduled) {
      return;
    }

    updateScheduledTriggerMutation.mutate(
      {
        trigger_id: trigger.trigger_id,
        updates,
      },
      {
        onError: (err) => setErrorText(err.message),
      },
    );
  }

  function handleCommonTriggerUpdate(
    updates: UpdateTriggerRequest | UpdateScheduledTriggerRequest,
  ) {
    if (trigger.trigger_type === TriggerType.EventDriven) {
      handleEventTriggerUpdate(updates);
    } else {
      handleScheduledTriggerUpdate(updates);
    }
  }

  return (
    <Box>
      {errorText && (
        <Box sx={{ mt: theme.spacing(1) }}>
          <Alert severity="error">{errorText}</Alert>
        </Box>
      )}
      <List sx={{ width: "48%" }}>
        <ListItem>
          <ListItemText primary={"Name"} secondary={trigger.name} />
        </ListItem>

        {trigger.trigger_type === TriggerType.EventDriven ? (
          <EditableTriggerCauses
            value={trigger.on_event!.causes}
            onChange={handleEventTriggerUpdate}
            setErrorText={setErrorText}
          />
        ) : (
          <EditableTriggerSchedule
            value={trigger.on_schedule!.schedule}
            onChange={handleScheduledTriggerUpdate}
            setErrorText={setErrorText}
          />
        )}

        <EditableActionSelector
          trigger={trigger}
          onChange={handleCommonTriggerUpdate}
          setErrorText={setErrorText}
        />

        {trigger.trigger_type === TriggerType.EventDriven ? (
          <EditableInputData
            title="Required Inputs"
            field="required_inputs"
            value={trigger.on_event!.required_inputs}
            onChange={handleEventTriggerUpdate}
            setErrorText={setErrorText}
            minRequired={1}
          />
        ) : (
          <EditableInvocationInput
            value={trigger.on_schedule!.invocation_input}
            onChange={handleScheduledTriggerUpdate}
            setErrorText={setErrorText}
          />
        )}

        {org?.tier === OrganizationTier.Premium ? (
          <EditableTimeout<UpdateTriggerRequest | UpdateScheduledTriggerRequest>
            value={trigger.timeout ?? undefined}
            onChange={handleCommonTriggerUpdate}
            setErrorText={setErrorText}
            renderMarkdown={false}
            canEdit={true}
          />
        ) : (
          <ListItem>
            <ListItemText
              primary={"Timeout"}
              secondaryTypographyProps={{
                component: "span",
                sx: {
                  fontSize: "0.8rem",
                },
              }}
              secondary={
                trigger.timeout ? <pre>{trigger.timeout} minutes</pre> : "None"
              }
            />
          </ListItem>
        )}

        {trigger.trigger_type === TriggerType.EventDriven && (
          <EditableInputData
            title="Additional Inputs"
            field="additional_inputs"
            value={trigger.on_event!.additional_inputs ?? []}
            onChange={handleEventTriggerUpdate}
            setErrorText={setErrorText}
            minRequired={0}
          />
        )}

        <EditableActionParams<
          UpdateTriggerRequest | UpdateScheduledTriggerRequest
        >
          value={parameters || []}
          paramValues={trigger.parameter_values ?? {}}
          onChange={handleCommonTriggerUpdate}
          setErrorText={setErrorText}
          canEdit={true}
        />

        {trigger.trigger_type === TriggerType.EventDriven && (
          <>
            <EditableTriggerCondition
              value={trigger.on_event!.condition ?? undefined}
              onChange={handleEventTriggerUpdate}
              setErrorText={setErrorText}
            />

            <EditableForEachSelector
              title={"Invoke For Each"}
              field={"for_each"}
              value={trigger.on_event!.for_each}
              onChange={handleEventTriggerUpdate}
              setErrorText={setErrorText}
            />
          </>
        )}

        <EditableContainerParams<
          UpdateTriggerRequest | UpdateScheduledTriggerRequest
        >
          title="Container Parameter Overrides"
          field="container_parameter_overrides"
          value={
            trigger.container_parameter_overrides ?? ({} as ContainerParameters)
          }
          onChange={handleCommonTriggerUpdate}
          setErrorText={setErrorText}
          overrides={true}
          canEdit={true}
        />

        <EditableComputeReqs<
          UpdateTriggerRequest | UpdateScheduledTriggerRequest
        >
          title="Compute Requirement Overrides"
          field="compute_requirement_overrides"
          value={
            trigger.compute_requirement_overrides ?? ({} as ComputeRequirements)
          }
          onChange={handleCommonTriggerUpdate}
          setErrorText={setErrorText}
          overrides={true}
          canEdit={true}
        />

        {trigger.trigger_type === TriggerType.Scheduled && (
          <EditableOutputDataset
            value={trigger.invocation_upload_destination}
            onChange={handleScheduledTriggerUpdate}
            setErrorText={setErrorText}
          />
        )}

        <ListItem>
          <ListItemText
            primary={"Operations"}
            secondaryTypographyProps={{
              component: "span",
            }}
            secondary={
              <Box sx={{ mt: theme.spacing(1) }}>
                <Box sx={{ mb: theme.spacing(2), ml: theme.spacing(0.5) }}>
                  <FormControlLabel
                    control={
                      <TriggerSwitch
                        active={trigger.enabled}
                        triggerId={trigger.trigger_id}
                        triggerName={trigger.name}
                        triggerType={trigger.trigger_type}
                        setErrorText={setErrorText}
                      />
                    }
                    label={
                      <Typography variant="body2">Enable Trigger</Typography>
                    }
                  />
                </Box>

                <DeleteTriggerButton
                  trigger={trigger}
                  onDelete={() => goto.actions({ tab: 1 })}
                  org={org}
                  setErrorText={setErrorText}
                />
              </Box>
            }
          />
        </ListItem>
      </List>
    </Box>
  );
};
